// Shared derivations for the trash table: the item-type metadata (tile icon +
// entity label + reference-code prefix), the row's display name, and the
// purge-countdown chip tone. Kept out of the column/page files so both render
// from one source (the activity-log module's split).

import { TrashItemType, type TrashRow } from '@/api/endpoints/trashApi';
import type { ChipVariant } from '@/components/common';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Item-type code → tile icon + entity label + reference prefix. The record is
 *  COMPLETE over the backend enum, so TS flags any new member it grows. */
export const ITEM_TYPE_META: Record<
  TrashItemType,
  { labelKey: string; icon: string; codePrefix: string }
> = {
  [TrashItemType.FORM_TEMPLATE]: {
    labelKey: 'trash_type_form_template',
    icon: 'file-text',
    codePrefix: 'FORM',
  },
  [TrashItemType.REPORT]: {
    labelKey: 'trash_type_report',
    icon: 'file-check',
    codePrefix: 'SUB',
  },
  [TrashItemType.INSPECTION]: {
    labelKey: 'trash_type_inspection',
    icon: 'clipboard-list',
    codePrefix: 'INSP',
  },
  [TrashItemType.TASK]: {
    labelKey: 'trash_type_task',
    icon: 'list-checks',
    codePrefix: 'TSK',
  },
  [TrashItemType.PROJECT]: {
    labelKey: 'trash_type_project',
    icon: 'folder-kanban',
    codePrefix: 'PRJ',
  },
  [TrashItemType.DATA_SOURCE]: {
    labelKey: 'trash_type_data_source',
    icon: 'database',
    codePrefix: 'DS',
  },
};

export const itemTypeLabel = (type: TrashItemType, t: Translate): string =>
  t(ITEM_TYPE_META[type].labelKey);

/** The row's primary label: the captured trash-time title, else the referenced
 *  document's reference code (reports are titled by their number), else null —
 *  the cell dashes. */
export const trashRowName = (row: TrashRow): string | null =>
  row.title ??
  (row.referenceNumber != null ? trashRowCode(row) : null);

/** The compact reference code under the name ("INSP-07"), when the referenced
 *  document carries a reference number. */
export const trashRowCode = (row: TrashRow): string | null =>
  row.referenceNumber != null
    ? `${ITEM_TYPE_META[row.itemType].codePrefix}-${String(row.referenceNumber).padStart(2, '0')}`
    : null;

/** Countdown tone: the last 3 days alarm, the last week warns, the rest stays
 *  neutral. */
export const purgeChipVariant = (daysLeft: number): ChipVariant =>
  daysLeft <= 3 ? 'danger' : daysLeft <= 7 ? 'warning' : 'neutral';
